import React, { useEffect, useState } from 'react';
import Navigation from './Navigation';
import Pagination from './Pagination';
import {
  EndpointHistoryRecord,
  EndpointHistoryResponse,
  EndpointQueryResponse,
  EndpointSuggestion,
} from '../services/endpointService';
import '../styles/Dashboard.css';
import '../styles/EndpointWorkbench.css';

interface EndpointWorkbenchProps {
  title: string;
  subtitle: string;
  endpointBadge: string;
  accent: 'hybrid' | 'aiprocess';
  queryButtonLabel: string;
  historyTitle: string;
  historySubtitle: string;
  presets: string[];
  queryPlaceholder: string;
  historyEmptyText: string;
  latestEmptyText: string;
  historyLagNote?: string;
  query: (defectDescription: string) => Promise<EndpointQueryResponse>;
  loadHistory: (params: { page: number; limit: number; search?: string }) => Promise<EndpointHistoryResponse>;
}

function formatTimestamp(timestamp?: string | null): string {
  if (!timestamp) return '-';

  try {
    return new Date(timestamp).toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '-';
  }
}

function formatStatusCode(statusCode?: number | null): string {
  if (!statusCode) return '-';
  return String(statusCode);
}

function formatConfidence(confidence?: number): string {
  if (typeof confidence !== 'number' || Number.isNaN(confidence)) {
    return '-';
  }

  return '%' + (confidence * 100).toFixed(2);
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return 'Veri gösterilemedi';
  }
}

function buildLocalRecord(endpoint: string, requestText: string, response: EndpointQueryResponse): EndpointHistoryRecord {
  const suggestions = Array.isArray(response.suggestions) ? response.suggestions : [];
  const topSuggestion = suggestions[0] || null;

  return {
    id: 'latest-' + Date.now(),
    endpoint,
    timestamp: response.timestamp || new Date().toISOString(),
    request: {
      defectDescription: requestText
    },
    requestText,
    response,
    success: Boolean(response.success),
    statusCode: response.success ? 200 : null,
    processingTime: response.metadata?.processingTime || null,
    similarDefectsFound: Number(response.similarDefectsFound || 0),
    suggestionCount: suggestions.length,
    historyId: response.historyId || null,
    savedToHistory: Boolean(response.metadata?.savedToHistory),
    topSuggestion: topSuggestion ? {
      generatedGroup: topSuggestion.generatedGroup || null,
      generatedParentGroup: topSuggestion.generatedParentGroup || null,
      defectDescriptionSuggestion: topSuggestion.defectDescriptionSuggestion || '',
      confidence: Number(topSuggestion.confidence || 0)
    } : null,
    username: null,
    source: 'app',
    clientIp: null
  };
}

function renderSuggestionCard(suggestion: EndpointSuggestion, accent: 'hybrid' | 'aiprocess') {
  return (
    <article key={suggestion.rank + '-' + (suggestion.id || suggestion.generatedGroup || 'suggestion')} className="endpoint-suggestion-card">
      <div className="endpoint-suggestion-head">
        <span className={"endpoint-rank endpoint-rank--" + accent}>#{suggestion.rank}</span>
        <span className="endpoint-confidence">Güven {formatConfidence(suggestion.confidence)}</span>
      </div>
      <h4>{suggestion.generatedGroup || '-'}</h4>
      <p className="endpoint-suggestion-parent">{suggestion.generatedParentGroup || 'Üst grup yok'}</p>
      <p className="endpoint-suggestion-text">{suggestion.defectDescriptionSuggestion || '-'}</p>
      {suggestion.generatedSolution && (
        <p className="endpoint-suggestion-solution">Çözüm: {suggestion.generatedSolution}</p>
      )}
    </article>
  );
}

const EndpointWorkbench: React.FC<EndpointWorkbenchProps> = ({
  title,
  subtitle,
  endpointBadge,
  accent,
  queryButtonLabel,
  historyTitle,
  historySubtitle,
  presets,
  queryPlaceholder,
  historyEmptyText,
  latestEmptyText,
  historyLagNote,
  query,
  loadHistory,
}) => {
  const [input, setInput] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [records, setRecords] = useState<EndpointHistoryRecord[]>([]);
  const [latestRecord, setLatestRecord] = useState<EndpointHistoryRecord | null>(null);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    let isCancelled = false;

    const fetchHistory = async () => {
      setIsLoadingHistory(true);
      setError('');

      try {
        const response = await loadHistory({
          page: currentPage,
          limit: pageSize,
          ...(searchTerm && { search: searchTerm })
        });

        if (isCancelled) {
          return;
        }

        if (response.success) {
          setRecords(response.data);
          setTotalRecords(response.pagination.totalRecords);
          setTotalPages(response.pagination.totalPages);
        }
      } catch (err: any) {
        if (!isCancelled) {
          setError(err.response?.data?.message || 'Geçmiş verileri yüklenirken hata oluştu');
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingHistory(false);
        }
      }
    };

    fetchHistory();

    return () => {
      isCancelled = true;
    };
  }, [currentPage, pageSize, searchTerm, refreshToken, loadHistory]);

  const handleQuerySubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const defectDescription = input.trim();

    if (!defectDescription || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await query(defectDescription);
      setLatestRecord(buildLocalRecord(endpointBadge, defectDescription, response));
      setInput('');
      setRefreshToken((value) => value + 1);
      setCurrentPage(1);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'İstek gönderilirken hata oluştu';
      setError(message);
      setLatestRecord(buildLocalRecord(endpointBadge, defectDescription, {
        success: false,
        message,
        error: err.response?.data?.error,
        suggestions: []
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setCurrentPage(1);
    setSearchTerm(searchInput.trim());
  };

  const toggleRow = (recordId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [recordId]: !prev[recordId]
    }));
  };

  const handleRefresh = () => {
    setRefreshToken((value) => value + 1);
  };

  const latestSuggestions = Array.isArray(latestRecord?.response?.suggestions)
    ? (latestRecord?.response?.suggestions || []).slice(0, 3)
    : [];

  return (
    <div className="container endpoint-page">
      <Navigation />

      <section className={"endpoint-hero endpoint-hero--" + accent}>
        <div>
          <p className="endpoint-kicker">Endpoint Workbench</p>
          <h1>{title}</h1>
          <p>{subtitle}</p>
        </div>
        <div className="endpoint-hero-meta">
          <span className={"endpoint-badge endpoint-badge--" + accent}>{endpointBadge}</span>
          <span className="endpoint-total-badge">Toplam kayıt {totalRecords.toLocaleString()}</span>
        </div>
      </section>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
          <button className="close-btn" onClick={() => setError('')}>✕</button>
        </div>
      )}

      <section className="endpoint-top-grid">
        <article className="endpoint-card endpoint-request-card">
          <div className="endpoint-card-head">
            <div>
              <h2>Request</h2>
              <p>Endpoint’e gönderilecek hata açıklamasını yazın.</p>
            </div>
            <span className="endpoint-card-chip">Canlı test</span>
          </div>

          <div className="endpoint-presets">
            {presets.map((preset) => (
              <button
                key={preset}
                type="button"
                className="endpoint-preset-btn"
                onClick={() => setInput(preset)}
              >
                {preset}
              </button>
            ))}
          </div>

          <form onSubmit={handleQuerySubmit} className="endpoint-query-form">
            <label className="endpoint-field-label" htmlFor={accent + '-query'}>
              defectDescription
            </label>
            <textarea
              id={accent + '-query'}
              className="endpoint-textarea"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder={queryPlaceholder}
              disabled={isSubmitting}
            />
            <div className="endpoint-form-actions">
              <button className="btn btn-primary endpoint-submit-btn" type="submit" disabled={isSubmitting || !input.trim()}>
                {isSubmitting ? 'Çalışıyor...' : queryButtonLabel}
              </button>
              <button className="btn btn-secondary" type="button" onClick={() => setInput('')} disabled={isSubmitting || !input}>
                Temizle
              </button>
            </div>
          </form>
        </article>

        <article className="endpoint-card endpoint-response-card">
          <div className="endpoint-card-head">
            <div>
              <h2>Response</h2>
              <p>Son çalışan sorgunun dönen yanıtı burada özetlenir.</p>
            </div>
            {latestRecord?.success ? (
              <span className="endpoint-status endpoint-status--success">Başarılı</span>
            ) : latestRecord ? (
              <span className="endpoint-status endpoint-status--error">Hatalı</span>
            ) : null}
          </div>

          {!latestRecord && (
            <div className="endpoint-empty-state">
              <p>{latestEmptyText}</p>
            </div>
          )}

          {latestRecord && (
            <div className="endpoint-response-body">
              <div className="endpoint-meta-grid">
                <div className="endpoint-meta-item">
                  <span className="endpoint-meta-label">Zaman</span>
                  <strong>{formatTimestamp(latestRecord.timestamp)}</strong>
                </div>
                <div className="endpoint-meta-item">
                  <span className="endpoint-meta-label">Benzer Hata</span>
                  <strong>{latestRecord.similarDefectsFound}</strong>
                </div>
                <div className="endpoint-meta-item">
                  <span className="endpoint-meta-label">Öneri</span>
                  <strong>{latestRecord.suggestionCount}</strong>
                </div>
                <div className="endpoint-meta-item">
                  <span className="endpoint-meta-label">İşlem Süresi</span>
                  <strong>{latestRecord.processingTime || '-'}</strong>
                </div>
              </div>

              <div className="endpoint-request-preview">
                <span className="endpoint-meta-label">Request Text</span>
                <p>{latestRecord.requestText || '-'}</p>
              </div>

              {latestRecord.historyId && (
                <div className="endpoint-inline-note">
                  <strong>History ID:</strong> {latestRecord.historyId}
                </div>
              )}

              {latestRecord.response?.message && !latestRecord.success && (
                <div className="endpoint-inline-note endpoint-inline-note--error">
                  {latestRecord.response.message}
                </div>
              )}

              {latestSuggestions.length > 0 && (
                <div className="endpoint-suggestions-grid">
                  {latestSuggestions.map((suggestion) => renderSuggestionCard(suggestion, accent))}
                </div>
              )}
            </div>
          )}
        </article>
      </section>

      <section className="endpoint-card endpoint-history-card">
        <div className="endpoint-card-head endpoint-history-head">
          <div>
            <h2>{historyTitle}</h2>
            <p>{historySubtitle}</p>
            {historyLagNote && <small className="endpoint-history-note">{historyLagNote}</small>}
          </div>
          <form className="endpoint-search-form" onSubmit={handleSearchSubmit}>
            <input
              type="text"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              className="endpoint-search-input"
              placeholder="Açıklama ara..."
            />
            <button className="btn btn-primary" type="submit">Ara</button>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => {
                setSearchInput('');
                setSearchTerm('');
                setCurrentPage(1);
              }}
              disabled={!searchInput && !searchTerm}
            >
              Temizle
            </button>
            <button className="btn btn-secondary" type="button" onClick={handleRefresh}>
              Yenile
            </button>
          </form>
        </div>

        {isLoadingHistory ? (
          <div className="loading endpoint-loading">
            <div className="spinner"></div>
            <p>Geçmiş yükleniyor...</p>
          </div>
        ) : records.length === 0 ? (
          <div className="endpoint-empty-state endpoint-empty-state--history">
            <p>{historyEmptyText}</p>
          </div>
        ) : (
          <>
            <div className="endpoint-history-list">
              {records.map((record) => {
                const isExpanded = Boolean(expandedRows[record.id]);
                const suggestions = Array.isArray(record.response?.suggestions)
                  ? (record.response?.suggestions || []).slice(0, 3)
                  : [];

                return (
                  <article key={record.id} className={"endpoint-history-item" + (isExpanded ? ' expanded' : '')}>
                    <button type="button" className="endpoint-history-summary" onClick={() => toggleRow(record.id)}>
                      <div className="endpoint-summary-main">
                        <div className="endpoint-summary-title-row">
                          <span className={"endpoint-status-pill endpoint-status-pill--" + (record.success ? 'success' : 'error')}>
                            {record.success ? 'Başarılı' : 'Hatalı'}
                          </span>
                          <span className="endpoint-summary-time">{formatTimestamp(record.timestamp)}</span>
                          <span className="endpoint-summary-code">HTTP {formatStatusCode(record.statusCode)}</span>
                          {record.source && (
                            <span className="endpoint-summary-code">{record.source === 'app' ? 'Uygulama' : 'Public'}</span>
                          )}
                        </div>
                        <p className="endpoint-summary-request">{record.requestText || '-'}</p>
                      </div>
                      <div className="endpoint-summary-stats">
                        <span>{record.similarDefectsFound} benzer</span>
                        <span>{record.suggestionCount} öneri</span>
                        <span>{record.processingTime || '-'}</span>
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="endpoint-history-details">
                        <div className="endpoint-history-details-grid">
                          <section className="endpoint-json-panel">
                            <div className="endpoint-panel-head">
                              <h3>Request</h3>
                              <span>JSON</span>
                            </div>
                            <pre>{safeStringify(record.request)}</pre>
                          </section>

                          <section className="endpoint-json-panel">
                            <div className="endpoint-panel-head">
                              <h3>Response</h3>
                              <span>JSON</span>
                            </div>
                            <pre>{safeStringify(record.response)}</pre>
                          </section>
                        </div>

                        {record.topSuggestion && (
                          <div className="endpoint-top-suggestion-bar">
                            <strong>En Güçlü Öneri:</strong>
                            <span>{record.topSuggestion.generatedGroup || '-'}</span>
                            <span>{record.topSuggestion.generatedParentGroup || '-'}</span>
                            <span>{formatConfidence(record.topSuggestion.confidence)}</span>
                          </div>
                        )}

                        {record.historyId && (
                          <div className="endpoint-inline-note">
                            <strong>History ID:</strong> {record.historyId}
                          </div>
                        )}

                        {suggestions.length > 0 && (
                          <div className="endpoint-suggestions-grid endpoint-suggestions-grid--history">
                            {suggestions.map((suggestion) => renderSuggestionCard(suggestion, accent))}
                          </div>
                        )}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalRecords}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setCurrentPage(1);
              }}
            />
          </>
        )}
      </section>
    </div>
  );
};

export default EndpointWorkbench;
